import React, { useState, useEffect } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import './Envase.css';

const hoyStr = () => new Date().toISOString().split('T')[0];

const fmt = (v, dec = 2) => {
    if (v == null || v === '') return '';
    return Number(v).toLocaleString('es-PY', {
        minimumFractionDigits: dec, maximumFractionDigits: dec
    });
};

const formatearFechaHora = (f) => {
    if (!f) return '';
    const d = new Date(f);
    return d.toLocaleString('es-PY', { hour12: false });
};

const formatearFecha = (f) => {
    if (!f) return '';
    const [y, m, d] = String(f).split('T')[0].split('-');
    return `${d}/${m}/${y}`;
};

// Chips por tipo de movimiento
const CHIPS_TIPO = {
    ENT_FAC: { color: '#2E7D32', bg: '#E8F5E9', label: 'ENT Factura' },
    ENT_REC: { color: '#2E7D32', bg: '#E8F5E9', label: 'ENT Recepcion' },
    ENT_TRA: { color: '#1565C0', bg: '#E3F2FD', label: 'ENT Transf.' },
    ENT_OTR: { color: '#00796B', bg: '#E0F2F1', label: 'ENT Otro' },
    SAL_REM: { color: '#C62828', bg: '#FFEBEE', label: 'SAL Remision' },
    SAL_VTA: { color: '#C62828', bg: '#FFEBEE', label: 'SAL Venta' },
    SAL_TRA: { color: '#1565C0', bg: '#E3F2FD', label: 'SAL Transf.' },
    SAL_OTR: { color: '#EF6C00', bg: '#FFF3E0', label: 'SAL Otro' },
    ANU_FAC: { color: '#6A1B9A', bg: '#F3E5F5', label: 'ANU Factura' },
    ANU_REC: { color: '#6A1B9A', bg: '#F3E5F5', label: 'ANU Recepcion' },
    ANU_REM: { color: '#6A1B9A', bg: '#F3E5F5', label: 'ANU Remision' },
    ANU_VTA: { color: '#6A1B9A', bg: '#F3E5F5', label: 'ANU Venta' },
    REA_FAC: { color: '#00838F', bg: '#E0F7FA', label: 'REA Factura' },
    REA_REC: { color: '#00838F', bg: '#E0F7FA', label: 'REA Recepcion' },
    REA_REM: { color: '#00838F', bg: '#E0F7FA', label: 'REA Remision' },
    REA_TRA: { color: '#00838F', bg: '#E0F7FA', label: 'REA Transf.' },
    AJU_STK: { color: '#5D4037', bg: '#EFEBE9', label: 'Ajuste' }
};

const chip = (tipo) => {
    const c = CHIPS_TIPO[tipo] || { color: '#333', bg: '#eee', label: tipo };
    return (
        <span style={{
            display: 'inline-block', padding: '2px 8px', borderRadius: '10px',
            backgroundColor: c.bg, color: c.color, fontSize: '11px', fontWeight: '600',
            whiteSpace: 'nowrap'
        }}>{c.label}</span>
    );
};

function Kardex({ usuario, initialTab = 'movs' }) {
    // === Tabs ===
    const [tab, setTab] = useState(initialTab); // movs | stockFecha | cierre
    // Si el prop cambia (navegacion entre menus), actualizar el tab
    useEffect(() => { setTab(initialTab); }, [initialTab]);

    // === Depositos (comun a todos los tabs) ===
    const [depositos, setDepositos] = useState([]);
    useEffect(() => {
        axios.get('/api/kardex/depositos').then(r => setDepositos(r.data)).catch(() => {});
    }, []);

    // ============================================================
    // Tab 1: Movimientos
    // ============================================================
    const [filtros, setFiltros] = useState({
        idProducto: '',
        idDeposito: '',
        fechaDesde: '',
        fechaHasta: '',
        tipoMov: '',
        docTipo: '',
        docRef: ''
    });
    const [pagina, setPagina] = useState(1);
    const [porPagina, setPorPagina] = useState(50);
    const [resultado, setResultado] = useState({ total: 0, filas: [] });
    const [cargando, setCargando] = useState(false);

    // Autocomplete producto
    const [busqProd, setBusqProd] = useState('');
    const [prodsEncontrados, setProdsEncontrados] = useState([]);
    const [nomProdSel, setNomProdSel] = useState('');
    useEffect(() => {
        if (busqProd.length < 2) { setProdsEncontrados([]); return; }
        let vigente = true;   // false si el texto cambio antes de llegar la respuesta
        const t = setTimeout(async () => {
            try {
                const r = await axios.get('/api/kardex/buscar-producto', { params: { busqueda: busqProd } });
                if (vigente) setProdsEncontrados(r.data);
            } catch (e) { console.error(e); }
        }, 250);
        return () => { vigente = false; clearTimeout(t); };
    }, [busqProd]);

    const seleccionarProd = (p) => {
        setFiltros(f => ({ ...f, idProducto: String(p.id_producto).trim() }));
        setNomProdSel(`${String(p.id_producto).trim()} - ${p.descripcion}`);
        setBusqProd(''); setProdsEncontrados([]);
    };

    const limpiarProd = () => {
        setFiltros(f => ({ ...f, idProducto: '' }));
        setNomProdSel('');
    };

    const buscarMovs = async (p = pagina) => {
        setCargando(true);
        try {
            const params = { ...filtros, pagina: p, porPagina };
            Object.keys(params).forEach(k => { if (params[k] === '' || params[k] == null) delete params[k]; });
            const { data } = await axios.get('/api/kardex', { params });
            setResultado(data);
            setPagina(data.pagina);
        } catch (e) {
            console.error(e);
            alert('Error al consultar kardex');
        } finally {
            setCargando(false);
        }
    };

    const totalPaginas = Math.max(1, Math.ceil((resultado.total || 0) / porPagina));

    const exportarMovs = () => {
        const filas = (resultado.filas || []).map(r => ({
            'Fecha/Hora': formatearFechaHora(r.fecha_hora),
            'Producto': r.id_producto,
            'Descripcion': r.desc_producto,
            'Deposito': r.desc_deposito || r.id_deposito,
            'Tipo': r.tipo_mov,
            'Cantidad': r.cantidad,
            'Precio': r.precio,
            'Valor mov': r.valor_mov,
            'Stk antes': r.stk_antes,
            'Stk despues': r.stk_despues,
            'Stk emp antes': r.stk_empresa_antes,
            'Stk emp despues': r.stk_empresa_despues,
            'Cp despues': r.costoprom_despues,
            'Doc tipo': r.doc_tipo,
            'Doc ref': r.doc_ref,
            'Operador': r.id_operador
        }));
        const ws = XLSX.utils.json_to_sheet(filas);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Kardex');
        XLSX.writeFile(wb, `kardex_${hoyStr()}.xlsx`);
    };

    // ============================================================
    // Tab 2: Stock a fecha (por producto+deposito, y por deposito)
    // ============================================================
    const [sfProd, setSfProd] = useState('');
    const [sfDep, setSfDep] = useState('');
    const [sfFecha, setSfFecha] = useState(hoyStr());
    const [sfResultado, setSfResultado] = useState(null);
    const [sfLista, setSfLista] = useState([]);
    const [sfModo, setSfModo] = useState('producto'); // producto | deposito

    const consultarStkFecha = async () => {
        if (sfModo === 'producto') {
            if (!sfProd || !sfDep) return alert('Seleccione producto y deposito');
            try {
                const { data } = await axios.get('/api/kardex/stock-a-fecha',
                    { params: { idProducto: sfProd, idDeposito: sfDep, fecha: sfFecha } });
                setSfResultado(data);
                setSfLista([]);
            } catch (e) { alert('Error al consultar'); }
        } else {
            if (!sfDep) return alert('Seleccione deposito');
            try {
                const { data } = await axios.get('/api/kardex/deposito-a-fecha',
                    { params: { idDeposito: sfDep, fecha: sfFecha } });
                setSfLista(data);
                setSfResultado(null);
            } catch (e) { alert('Error al consultar'); }
        }
    };

    // ============================================================
    // Tab 3: Cierre diario
    // ============================================================
    const [cierres, setCierres] = useState([]);
    const [fechaCerrar, setFechaCerrar] = useState(hoyStr());
    const [cerrando, setCerrando] = useState(false);

    const cargarCierres = async () => {
        try {
            const { data } = await axios.get('/api/kardex/cierres');
            setCierres(data);
        } catch (e) { console.error(e); }
    };
    useEffect(() => { if (tab === 'cierre') cargarCierres(); }, [tab]);

    const ejecutarCierre = async () => {
        if (!fechaCerrar) return;
        const yaCerrado = cierres.find(c => c.fecha === fechaCerrar);
        if (yaCerrado && !window.confirm(
            `El dia ${formatearFecha(fechaCerrar)} ya esta cerrado. Rehacer el cierre?`)) return;
        setCerrando(true);
        try {
            const { data } = await axios.post('/api/kardex/cierre',
                { fecha: fechaCerrar, operador: usuario || 'sistema' });
            alert(`Cierre OK.\nProductos: ${data.productos}\nValor total: ${fmt(data.valor_total)}\nMovimientos del dia: ${data.movimientos_dia}`);
            cargarCierres();
        } catch (e) {
            alert(e.response?.data?.message || 'Error al cerrar');
        } finally {
            setCerrando(false);
        }
    };

    const borrarCierre = async (fecha) => {
        if (!window.confirm(`Borrar cierre del ${formatearFecha(fecha)}?`)) return;
        try {
            await axios.delete(`/api/kardex/cierre/${fecha}`);
            cargarCierres();
        } catch (e) {
            alert(e.response?.data?.message || 'Error al borrar');
        }
    };

    // ============================================================
    // Render
    // ============================================================
    return (
        <div style={{ padding: '20px', fontFamily: 'Gilam, sans-serif' }}>
            <h2 style={{ color: '#004F9F', marginBottom: '15px' }}>
                Kardex de Stock <span style={{ fontSize: '13px', color: '#666', fontWeight: 'normal' }}>
                    · auditoria dia-a-dia
                </span>
            </h2>

            {/* Tabs */}
            <div style={{ display: 'flex', gap: '2px', borderBottom: '2px solid #004F9F', marginBottom: '15px' }}>
                {[
                    { k: 'movs',       l: '📖 Movimientos' },
                    { k: 'stockFecha', l: '📅 Stock a fecha' },
                    { k: 'cierre',     l: '🔒 Cierre diario' }
                ].map(t => (
                    <button key={t.k} onClick={() => setTab(t.k)}
                        style={{
                            padding: '8px 20px',
                            background: tab === t.k ? '#004F9F' : '#f5f5f5',
                            color: tab === t.k ? '#fff' : '#555',
                            border: 'none',
                            borderRadius: '4px 4px 0 0',
                            cursor: 'pointer',
                            fontWeight: tab === t.k ? '600' : 'normal'
                        }}>{t.l}</button>
                ))}
            </div>

            {/* ========================= TAB MOVIMIENTOS ========================= */}
            {tab === 'movs' && (
                <div>
                    <div style={{ padding: '12px', background: '#F5F9FE', borderRadius: '6px',
                                 marginBottom: '10px', display: 'grid',
                                 gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px' }}>
                        {/* Producto */}
                        <div style={{ gridColumn: 'span 2', position: 'relative' }}>
                            <label style={{ fontSize: '12px', color: '#555' }}>Producto</label>
                            {filtros.idProducto ? (
                                <div style={{ display: 'flex', gap: '6px', padding: '6px 8px',
                                             border: '1px solid #ddd', borderRadius: '4px',
                                             background: '#E4F2FC' }}>
                                    <strong>{nomProdSel}</strong>
                                    <button type="button" onClick={limpiarProd}
                                        style={{ marginLeft: 'auto', background: 'none', border: 'none',
                                                 color: '#C62828', cursor: 'pointer' }}>✕</button>
                                </div>
                            ) : (
                                <input type="text" value={busqProd}
                                    onChange={(e) => setBusqProd(e.target.value)}
                                    placeholder="Buscar por codigo o descripcion"
                                    style={{ width: '100%', padding: '6px', border: '1px solid #ddd',
                                             borderRadius: '4px' }} />
                            )}
                            {prodsEncontrados.length > 0 && (
                                <div style={{
                                    position: 'absolute', top: '100%', left: 0, right: 0,
                                    background: '#fff', border: '1px solid #004F9F',
                                    borderRadius: '4px', maxHeight: '200px', overflowY: 'auto',
                                    zIndex: 20, boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                                }}>
                                    {prodsEncontrados.map(p => (
                                        <div key={p.id_producto} onClick={() => seleccionarProd(p)}
                                            style={{ padding: '6px 10px', cursor: 'pointer',
                                                     borderBottom: '1px solid #eee' }}
                                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#E4F2FC'}
                                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#fff'}>
                                            <strong>{String(p.id_producto).trim()}</strong> · {p.descripcion}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                        <div>
                            <label style={{ fontSize: '12px', color: '#555' }}>Deposito</label>
                            <select value={filtros.idDeposito}
                                onChange={(e) => setFiltros(f => ({ ...f, idDeposito: e.target.value }))}
                                style={{ width: '100%', padding: '6px', border: '1px solid #ddd', borderRadius: '4px' }}>
                                <option value="">(Todos)</option>
                                {depositos.map(d => (
                                    <option key={d.id_deposito} value={d.id_deposito}>
                                        {d.id_deposito} - {d.nom_depto}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label style={{ fontSize: '12px', color: '#555' }}>Tipo movimiento</label>
                            <select value={filtros.tipoMov}
                                onChange={(e) => setFiltros(f => ({ ...f, tipoMov: e.target.value }))}
                                style={{ width: '100%', padding: '6px', border: '1px solid #ddd', borderRadius: '4px' }}>
                                <option value="">(Todos)</option>
                                {Object.keys(CHIPS_TIPO).map(k => (
                                    <option key={k} value={k}>{CHIPS_TIPO[k].label}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label style={{ fontSize: '12px', color: '#555' }}>Fecha desde</label>
                            <input type="date" value={filtros.fechaDesde}
                                onChange={(e) => setFiltros(f => ({ ...f, fechaDesde: e.target.value }))}
                                style={{ width: '100%', padding: '6px', border: '1px solid #ddd', borderRadius: '4px' }} />
                        </div>
                        <div>
                            <label style={{ fontSize: '12px', color: '#555' }}>Fecha hasta</label>
                            <input type="date" value={filtros.fechaHasta}
                                onChange={(e) => setFiltros(f => ({ ...f, fechaHasta: e.target.value }))}
                                style={{ width: '100%', padding: '6px', border: '1px solid #ddd', borderRadius: '4px' }} />
                        </div>
                        <div>
                            <label style={{ fontSize: '12px', color: '#555' }}>Documento (parcial)</label>
                            <input type="text" value={filtros.docRef}
                                onChange={(e) => setFiltros(f => ({ ...f, docRef: e.target.value }))}
                                placeholder="ej: FCR-15"
                                style={{ width: '100%', padding: '6px', border: '1px solid #ddd', borderRadius: '4px' }} />
                        </div>
                        <div style={{ display: 'flex', alignItems: 'end', gap: '8px' }}>
                            <button onClick={() => buscarMovs(1)}
                                style={{ padding: '8px 20px', background: '#004F9F', color: '#fff',
                                         border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: '600' }}>
                                🔍 Buscar
                            </button>
                            <button onClick={exportarMovs} disabled={resultado.filas?.length === 0}
                                style={{ padding: '8px 12px', background: '#4CAF50', color: '#fff',
                                         border: 'none', borderRadius: '4px',
                                         cursor: resultado.filas?.length ? 'pointer' : 'not-allowed',
                                         opacity: resultado.filas?.length ? 1 : 0.5 }}>
                                📊 Excel
                            </button>
                        </div>
                    </div>

                    <div style={{ overflowX: 'auto', border: '1px solid #e0e0e0', borderRadius: '4px' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                            <thead style={{ background: '#004F9F', color: '#fff' }}>
                                <tr>
                                    <th style={{ padding: '6px', textAlign: 'left' }}>Fecha/Hora</th>
                                    <th style={{ padding: '6px', textAlign: 'left' }}>Producto</th>
                                    <th style={{ padding: '6px', textAlign: 'left' }}>Deposito</th>
                                    <th style={{ padding: '6px', textAlign: 'center' }}>Tipo</th>
                                    <th style={{ padding: '6px', textAlign: 'right' }}>Cantidad</th>
                                    <th style={{ padding: '6px', textAlign: 'right' }}>Precio</th>
                                    <th style={{ padding: '6px', textAlign: 'right' }}>Valor</th>
                                    <th style={{ padding: '6px', textAlign: 'right' }}>Stk antes</th>
                                    <th style={{ padding: '6px', textAlign: 'right' }}>Stk desp.</th>
                                    <th style={{ padding: '6px', textAlign: 'right' }}>Cp desp.</th>
                                    <th style={{ padding: '6px', textAlign: 'left' }}>Documento</th>
                                    <th style={{ padding: '6px', textAlign: 'left' }}>Operador</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(resultado.filas || []).map(r => (
                                    <tr key={r.id} style={{ borderBottom: '1px solid #eee' }}>
                                        <td style={{ padding: '4px 6px', whiteSpace: 'nowrap' }}>{formatearFechaHora(r.fecha_hora)}</td>
                                        <td style={{ padding: '4px 6px' }}>
                                            <strong>{String(r.id_producto).trim()}</strong>
                                            <div style={{ color: '#666', fontSize: '11px' }}>{r.desc_producto}</div>
                                        </td>
                                        <td style={{ padding: '4px 6px' }}>{r.desc_deposito || r.id_deposito}</td>
                                        <td style={{ padding: '4px 6px', textAlign: 'center' }}>{chip(r.tipo_mov)}</td>
                                        <td style={{ padding: '4px 6px', textAlign: 'right',
                                                     color: Number(r.cantidad) < 0 ? '#C62828' : '#2E7D32',
                                                     fontWeight: '600' }}>
                                            {Number(r.cantidad) > 0 ? '+' : ''}{fmt(r.cantidad, 3)}
                                        </td>
                                        <td style={{ padding: '4px 6px', textAlign: 'right' }}>{fmt(r.precio, 2)}</td>
                                        <td style={{ padding: '4px 6px', textAlign: 'right' }}>{fmt(r.valor_mov, 2)}</td>
                                        <td style={{ padding: '4px 6px', textAlign: 'right', color: '#666' }}>{fmt(r.stk_antes, 2)}</td>
                                        <td style={{ padding: '4px 6px', textAlign: 'right', fontWeight: '600' }}>{fmt(r.stk_despues, 2)}</td>
                                        <td style={{ padding: '4px 6px', textAlign: 'right' }}>{fmt(r.costoprom_despues, 2)}</td>
                                        <td style={{ padding: '4px 6px', fontSize: '11px' }}>
                                            <span style={{ color: '#004F9F', fontWeight: '600' }}>{r.doc_tipo}</span>
                                            <div style={{ color: '#888' }}>{r.doc_ref}</div>
                                        </td>
                                        <td style={{ padding: '4px 6px', fontSize: '11px', color: '#666' }}>{r.id_operador}</td>
                                    </tr>
                                ))}
                                {(!resultado.filas || resultado.filas.length === 0) && (
                                    <tr><td colSpan="12" style={{ padding: '20px', textAlign: 'center', color: '#888' }}>
                                        {cargando ? 'Cargando...' : 'Sin movimientos. Ajuste los filtros y presione Buscar.'}
                                    </td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Paginacion */}
                    {resultado.total > 0 && (
                        <div style={{ marginTop: '10px', display: 'flex', gap: '8px', alignItems: 'center' }}>
                            <button onClick={() => buscarMovs(pagina - 1)} disabled={pagina === 1}>‹</button>
                            <span>Pagina {pagina} de {totalPaginas} · {resultado.total} movimiento(s)</span>
                            <button onClick={() => buscarMovs(pagina + 1)} disabled={pagina >= totalPaginas}>›</button>
                            <select value={porPagina} onChange={(e) => { setPorPagina(Number(e.target.value)); }}>
                                {[25, 50, 100, 250].map(n => <option key={n} value={n}>{n} / pag</option>)}
                            </select>
                        </div>
                    )}
                </div>
            )}

            {/* ========================= TAB STOCK A FECHA ========================= */}
            {tab === 'stockFecha' && (
                <div>
                    <div style={{ padding: '15px', background: '#F5F9FE', borderRadius: '6px', marginBottom: '10px' }}>
                        <div style={{ display: 'flex', gap: '15px', marginBottom: '10px' }}>
                            <label>
                                <input type="radio" checked={sfModo === 'producto'}
                                    onChange={() => setSfModo('producto')} />
                                {' '}Un producto en un deposito
                            </label>
                            <label>
                                <input type="radio" checked={sfModo === 'deposito'}
                                    onChange={() => setSfModo('deposito')} />
                                {' '}Todos los productos de un deposito
                            </label>
                        </div>
                        <div style={{ display: 'grid',
                                     gridTemplateColumns: sfModo === 'producto' ? '1fr 1fr 1fr auto' : '1fr 1fr auto',
                                     gap: '10px', alignItems: 'end' }}>
                            {sfModo === 'producto' && (
                                <div>
                                    <label style={{ fontSize: '12px', color: '#555' }}>ID Producto</label>
                                    <input type="text" value={sfProd}
                                        onChange={(e) => setSfProd(e.target.value)}
                                        placeholder="ej: 9801"
                                        style={{ width: '100%', padding: '6px', border: '1px solid #ddd',
                                                 borderRadius: '4px' }} />
                                </div>
                            )}
                            <div>
                                <label style={{ fontSize: '12px', color: '#555' }}>Deposito</label>
                                <select value={sfDep} onChange={(e) => setSfDep(e.target.value)}
                                    style={{ width: '100%', padding: '6px', border: '1px solid #ddd',
                                             borderRadius: '4px' }}>
                                    <option value="">-- seleccionar --</option>
                                    {depositos.map(d => (
                                        <option key={d.id_deposito} value={d.id_deposito}>
                                            {d.id_deposito} - {d.nom_depto}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label style={{ fontSize: '12px', color: '#555' }}>Fecha</label>
                                <input type="date" value={sfFecha}
                                    onChange={(e) => setSfFecha(e.target.value)}
                                    style={{ width: '100%', padding: '6px', border: '1px solid #ddd',
                                             borderRadius: '4px' }} />
                            </div>
                            <button onClick={consultarStkFecha}
                                style={{ padding: '8px 20px', background: '#004F9F', color: '#fff',
                                         border: 'none', borderRadius: '4px', cursor: 'pointer',
                                         fontWeight: '600' }}>
                                Consultar
                            </button>
                        </div>
                    </div>

                    {sfResultado && (
                        <div style={{ padding: '20px', border: '1px solid #4CAF50', borderRadius: '6px',
                                     background: '#F1F8E9' }}>
                            <div style={{ fontSize: '14px', color: '#555' }}>
                                Producto <strong>{sfResultado.idProducto}</strong> en deposito{' '}
                                <strong>{sfResultado.idDeposito}</strong> al{' '}
                                <strong>{formatearFecha(sfResultado.fecha)}</strong>:
                            </div>
                            <div style={{ fontSize: '32px', color: '#2E7D32', fontWeight: 'bold', marginTop: '8px' }}>
                                {fmt(sfResultado.existencia, 3)} unidades
                            </div>
                        </div>
                    )}

                    {sfLista.length > 0 && (
                        <div style={{ overflowX: 'auto', border: '1px solid #e0e0e0', borderRadius: '4px' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                                <thead style={{ background: '#004F9F', color: '#fff' }}>
                                    <tr>
                                        <th style={{ padding: '6px', textAlign: 'left' }}>Cod</th>
                                        <th style={{ padding: '6px', textAlign: 'left' }}>Descripcion</th>
                                        <th style={{ padding: '6px', textAlign: 'right' }}>Existencia</th>
                                        <th style={{ padding: '6px', textAlign: 'right' }}>Cp</th>
                                        <th style={{ padding: '6px', textAlign: 'right' }}>Valor</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {sfLista.map(r => (
                                        <tr key={r.id_producto} style={{ borderBottom: '1px solid #eee' }}>
                                            <td style={{ padding: '4px 6px' }}>{String(r.id_producto).trim()}</td>
                                            <td style={{ padding: '4px 6px' }}>{r.descripcion}</td>
                                            <td style={{ padding: '4px 6px', textAlign: 'right' }}>{fmt(r.existencia, 3)}</td>
                                            <td style={{ padding: '4px 6px', textAlign: 'right' }}>{fmt(r.costoprom, 2)}</td>
                                            <td style={{ padding: '4px 6px', textAlign: 'right', fontWeight: '600' }}>{fmt(r.valor, 2)}</td>
                                        </tr>
                                    ))}
                                    <tr style={{ background: '#E4F2FC', fontWeight: 'bold' }}>
                                        <td colSpan="4" style={{ padding: '6px', textAlign: 'right' }}>TOTAL:</td>
                                        <td style={{ padding: '6px', textAlign: 'right' }}>
                                            {fmt(sfLista.reduce((s, r) => s + Number(r.valor || 0), 0), 2)}
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}

            {/* ========================= TAB CIERRE ========================= */}
            {tab === 'cierre' && (
                <div>
                    <div style={{ padding: '15px', background: '#FFF3E0', borderRadius: '6px', marginBottom: '15px',
                                 border: '1px solid #FFB300' }}>
                        <h4 style={{ margin: '0 0 8px 0', color: '#E65100' }}>Cerrar dia (tomar snapshot)</h4>
                        <div style={{ display: 'flex', gap: '10px', alignItems: 'end', flexWrap: 'wrap' }}>
                            <div>
                                <label style={{ fontSize: '12px', color: '#555' }}>Fecha a cerrar</label>
                                <input type="date" value={fechaCerrar}
                                    onChange={(e) => setFechaCerrar(e.target.value)}
                                    style={{ padding: '6px', border: '1px solid #ddd', borderRadius: '4px' }} />
                            </div>
                            <button onClick={ejecutarCierre} disabled={cerrando}
                                style={{ padding: '8px 20px', background: '#E65100', color: '#fff',
                                         border: 'none', borderRadius: '4px',
                                         cursor: cerrando ? 'wait' : 'pointer', fontWeight: '600' }}>
                                {cerrando ? 'Cerrando...' : '🔒 Cerrar dia'}
                            </button>
                            <span style={{ fontSize: '12px', color: '#666' }}>
                                Guarda existencia, costo y valor de cada producto+deposito al final del dia.
                            </span>
                        </div>
                    </div>

                    <h4 style={{ color: '#004F9F' }}>Cierres realizados</h4>
                    <div style={{ overflowX: 'auto', border: '1px solid #e0e0e0', borderRadius: '4px' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                            <thead style={{ background: '#004F9F', color: '#fff' }}>
                                <tr>
                                    <th style={{ padding: '6px', textAlign: 'left' }}>Fecha</th>
                                    <th style={{ padding: '6px', textAlign: 'right' }}>Productos</th>
                                    <th style={{ padding: '6px', textAlign: 'right' }}>Valor total</th>
                                    <th style={{ padding: '6px', textAlign: 'right' }}>Movs. del dia</th>
                                    <th style={{ padding: '6px', textAlign: 'left' }}>Operador</th>
                                    <th style={{ padding: '6px', textAlign: 'left' }}>Fch. cierre</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {cierres.map(c => (
                                    <tr key={c.fecha} style={{ borderBottom: '1px solid #eee' }}>
                                        <td style={{ padding: '4px 6px', fontWeight: '600', color: '#004F9F' }}>
                                            {formatearFecha(c.fecha)}
                                        </td>
                                        <td style={{ padding: '4px 6px', textAlign: 'right' }}>{c.productos}</td>
                                        <td style={{ padding: '4px 6px', textAlign: 'right', fontWeight: '600' }}>
                                            {fmt(c.valor_total, 2)}
                                        </td>
                                        <td style={{ padding: '4px 6px', textAlign: 'right' }}>{c.movimientos_dia}</td>
                                        <td style={{ padding: '4px 6px', fontSize: '11px' }}>{c.id_operador}</td>
                                        <td style={{ padding: '4px 6px', fontSize: '11px', color: '#888' }}>{formatearFechaHora(c.fchcierre)}</td>
                                        <td style={{ padding: '4px 6px' }}>
                                            <button onClick={() => borrarCierre(c.fecha)} title="Borrar cierre"
                                                style={{ background: 'none', border: '1px solid #C62828',
                                                         color: '#C62828', borderRadius: '4px',
                                                         padding: '2px 8px', cursor: 'pointer' }}>🗑</button>
                                        </td>
                                    </tr>
                                ))}
                                {cierres.length === 0 && (
                                    <tr><td colSpan="7" style={{ padding: '20px', textAlign: 'center', color: '#888' }}>
                                        Aun no se ejecuto ningun cierre.
                                    </td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}

export default Kardex;
